import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  List,
  useDataGrid,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  BaseRecord,
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Grid,
  TextField,
  CircularProgress,
  Tooltip,
  Switch,
  Typography,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import axiosInstance from "../../api/axios-config";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NearMeIcon from '@mui/icons-material/NearMe';
import NearMeDisabledIcon from '@mui/icons-material/NearMeDisabled';

import { useAuth } from "../../contexts/auth-context";
import { useGeocoding } from "../../hooks/useGeocoding";

// Add custom styles for profile markers
const customMarkerStyles = `
  .custom-profile-marker {
    background: transparent !important;
    border: none !important;
  }

  .custom-profile-marker img {
    transition: transform 0.2s ease;
  }

  .leaflet-popup-content {
    margin: 12px !important;
  }

  .leaflet-popup-content-wrapper {
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .location-pulse {
    animation: pulse 2s ease-in-out infinite;
  }
`;

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export interface IUser {
  id: number;
  userId: number;
  name: string;
  profilePicture?: string;
  profilePictureUrl?: string;
  role?: string;
  isSharing?: boolean;
  location?: {
    latitude: number;
    longitude: number;
    timestamp: string;
  };
}

export interface IUserFilterVariables {
  q?: string;
  name?: string;
}

// Add deep comparison for user locations
const areUsersEqual = (prevProps: { users: IUser[] }, nextProps: { users: IUser[] }) => {
  const prevUsers = prevProps.users;
  const nextUsers = nextProps.users;

  if (prevUsers.length !== nextUsers.length) return false;

  return prevUsers.every((prevUser, index) => {
    const nextUser = nextUsers[index];
    return prevUser.id === nextUser.id &&
           prevUser.location?.latitude === nextUser.location?.latitude &&
           prevUser.location?.longitude === nextUser.location?.longitude;
  });
};

// Add MapView component
const MapView = React.memo(({ users }: { users: IUser[] }) => {
  const mapRef = useRef<any>(null);
  const maxZoom = 18;
  const minZoom = 2;

  // Calculate center and bounds based on visible users
  const { center, bounds } = useMemo(() => {
    if (!users.length) {
      return {
        center: [43.29695, 5.38107] as [number, number],
        bounds: null
      };
    }

    const locations = users
      .filter((user: IUser) => user.location)
      .map((user: IUser) => [user.location!.latitude, user.location!.longitude] as [number, number]);

    if (!locations.length) {
      return {
        center: [43.29695, 5.38107] as [number, number],
        bounds: null
      };
    }

    // Calculate center
    const center = locations.reduce<[number, number]>((acc, [lat, lng]) => {
      acc[0] += lat;
      acc[1] += lng;
      return acc;
    }, [0, 0]).map((coord: number) => coord / locations.length) as [number, number];

    // Calculate bounds
    const bounds = locations.reduce<[number, number, number, number]>((acc, [lat, lng]) => {
      acc[0] = Math.min(acc[0], lat);
      acc[1] = Math.min(acc[1], lng);
      acc[2] = Math.max(acc[2], lat);
      acc[3] = Math.max(acc[3], lng);
      return acc;
    }, [Infinity, Infinity, -Infinity, -Infinity]);

    return { center, bounds };
  }, [users]);

  // Update map bounds when they change
  useEffect(() => {
    if (mapRef.current && bounds && bounds[0] !== Infinity) {
      // Only fit bounds if we have valid bounds and more than one user
      const latDiff = bounds[2] - bounds[0];
      const lngDiff = bounds[3] - bounds[1];

      // If the area is too small, don't zoom in too much
      if (latDiff > 0.001 || lngDiff > 0.001) {
        mapRef.current.fitBounds([
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]]
        ], {
          padding: [50, 50],
          maxZoom: 15
        });
      } else {
        // For single point or very close points, just center without extreme zoom
        mapRef.current.setView(center, 14);
      }
    }
  }, [bounds, center]);

  // Auto-center and zoom on initial load/refresh
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && bounds && bounds[0] !== Infinity) {
        const latDiff = bounds[2] - bounds[0];
        const lngDiff = bounds[3] - bounds[1];

        if (latDiff > 0.001 || lngDiff > 0.001) {
          mapRef.current.fitBounds([
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]]
          ], {
            padding: [50, 50],
            maxZoom: 15
          });
        } else {
          mapRef.current.setView(center, 14);
        }
      }
    }, 100); // Small delay to ensure map is fully loaded

    return () => clearTimeout(timer);
  }, [bounds, center, users.length]); // Trigger when users are loaded for the first time

  return (
    <>
      <style>{customMarkerStyles}</style>
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={13}
        minZoom={minZoom}
        maxZoom={maxZoom}
        style={{ height: "100%", width: "100%" }}
        maxBounds={[[-90, -180], [90, 180]]}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
          maxZoom={maxZoom}
          minZoom={minZoom}
        />

        {users.map((user: IUser) => (
          <UserMarker key={user.id} user={user} />
        ))}
      </MapContainer>
    </>
  );
}, areUsersEqual);

// Popup component with reverse geocoding
const UserLocationPopup: React.FC<{ user: IUser }> = ({ user }) => {
  const translate = useTranslate();
  const { address, loading: isLoading } = useGeocoding(
    user.location?.latitude,
    user.location?.longitude,
    { priority: 1 } // Higher priority for popups
  );

  return (
    <div style={{ minWidth: '250px', padding: '8px' }}>
      {/* Header with profile picture and name */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <img
          src={user.profilePictureUrl || user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
          alt={user.name}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            marginRight: '12px',
            border: '2px solid #e0e0e0',
            objectFit: 'cover'
          }}
          onError={(e) => {
            // Fallback to a different avatar style if the first one fails
            e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${user.name}`;
          }}
        />
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '2px' }}>
            {user.name}
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {user.role === 'MODERATOR' ? translate("user.role.moderator") : translate("user.role.member")}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{
        borderTop: '1px solid #eee',
        margin: '8px -8px',
        width: 'calc(100% + 16px)'
      }} />

      {/* Location information */}
      <div style={{ marginTop: '8px' }}>
        {/* Address */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px', marginRight: '8px' }}>📍</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
              {translate("location.address")}
            </div>
            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
              {isLoading ? (
                <span style={{ color: '#999' }}>{address}</span>
              ) : (
                address
              )}
            </div>
          </div>
        </div>

        {/* Coordinates */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '16px', marginRight: '8px' }}>🧭</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
              {translate("location.coordinates")}
            </div>
            <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {user.location?.latitude.toFixed(6)}, {user.location?.longitude.toFixed(6)}
            </div>
          </div>
        </div>

        {/* Last update */}
        {user.location?.timestamp && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '16px', marginRight: '8px' }}>🕐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
                {translate("location.lastUpdate")}
              </div>
              <div style={{ fontSize: '13px' }}>
                {new Date(user.location.timestamp).toLocaleString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: '2-digit',
                  month: 'short'
                })}
              </div>
            </div>
          </div>
        )}

        {/* Status */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '16px', marginRight: '8px' }}>
            {user.isSharing ? '🟢' : '🔴'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>
              {translate("location.status.label")}
            </div>
            <div style={{ fontSize: '13px', color: user.isSharing ? '#4CAF50' : '#f44336' }}>
              {user.isSharing ? translate("location.status.active") : translate("location.status.inactive")}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
        <Button
          size="small"
          variant="outlined"
          fullWidth
          style={{
            textTransform: 'none',
            borderRadius: '8px'
          }}
          startIcon={<NearMeIcon style={{ fontSize: '16px' }} />}
          onClick={() => {
            if (user.location) {
              window.open(
                `https://www.google.com/maps?q=${user.location.latitude},${user.location.longitude}`,
                '_blank'
              );
            }
          }}
        >
          {translate("location.openInGoogleMaps")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          fullWidth
          style={{
            textTransform: 'none',
            borderRadius: '8px'
          }}
          onClick={() => {
            if (user.location) {
              window.open(
                `https://www.google.com/maps?q=&layer=c&cbll=${user.location.latitude},${user.location.longitude}`,
                '_blank'
              );
            }
          }}
        >
          🗺️ Street View
        </Button>
      </div>
    </div>
  );
};

// Add UserMarker component with profile pictures
const UserMarker: React.FC<{ user: IUser }> = React.memo(({ user }) => {
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (markerRef.current) {
      // Create a custom DivIcon with the user's profile picture
      const profilePicUrl = user.profilePictureUrl || user.profilePicture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`;

      // Create HTML for the custom marker
      const markerHtml = `
        <div style="
          position: relative;
          width: fit-content;
          height: fit-content;
        ">
          <div style="
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            border: 3px solid #1976d2;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            background: #1976d2;
          ">
            <img
              src="${profilePicUrl}"
              alt="${user.name}"
              style="
                width: 100%;
                height: 100%;
                object-fit: cover;
              "
              onerror="this.src='https://api.dicebear.com/7.x/initials/svg?seed=${
                user.name
              }'"
            />
          </div>
          <div style="
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 10px solid #1976d2;
            filter: drop-shadow(0 2px 3px rgba(0,0,0,0.2));
          "></div>
          ${
            user.isSharing
              ? `
            <div class="${
              user.location &&
              new Date().getTime() -
                new Date(user.location.timestamp).getTime() <
                600000
                ? "location-pulse"
                : ""
            }" style="
              position: absolute;
              top: 0px;
              right: 0px;
              width: 12px;
              height: 12px;
              background: ${
                user.location &&
                new Date().getTime() -
                  new Date(user.location.timestamp).getTime() <
                  300000
                  ? "#4CAF50"
                  : "#FFC107"
              };
              border: 2px solid #1976d2;
              border-radius: 50%;
              // box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            "></div>
          `
              : ""
          }
        </div>
      `;

      const customIcon = L.divIcon({
        html: markerHtml,
        iconSize: [50, 50],
        iconAnchor: [25, 50],
        popupAnchor: [0, -50],
        className: 'custom-profile-marker'
      });

      markerRef.current.setIcon(customIcon);
    }
  }, [user.id, user.profilePictureUrl, user.profilePicture, user.name, user.isSharing, user.location]);

  if (!user.location) {
    return null;
  }

  return (
    <Marker
      ref={markerRef}
      position={[
        user.location.latitude,
        user.location.longitude,
      ]}
    >
      <Popup>
        <UserLocationPopup user={user} />
      </Popup>
    </Marker>
  );
});

export const LocationList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const [checkedUsers, setCheckedUsers] = useState<Set<number>>(new Set());
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // State for loading and filtered users
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [usersWithLocation, setUsersWithLocation] = useState<IUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);

  // State for active filters
  const [activeFilters, setActiveFilters] = useState<IUserFilterVariables>({});

  // State for pagination
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 8,
    page: 0,
  });

  // Memoize filtered data
  const filteredData = useMemo(() => {
    if (!usersWithLocation.length) return [];

    let result = [...usersWithLocation];
    const { name } = activeFilters;

    if (name && name !== "") {
      result = result.filter((user) => {
        if (!user.name) return false;
        return user.name.toLowerCase().includes(name.toLowerCase());
      });
    }

    if (checkedUsers.size > 0) {
      result = result.filter((user) => checkedUsers.has(user.id));
    }

    // Sort by timestamp (newest first)
    result.sort((a, b) => {
      const timeA = a.location?.timestamp
        ? new Date(a.location.timestamp).getTime()
        : 0;
      const timeB = b.location?.timestamp
        ? new Date(b.location.timestamp).getTime()
        : 0;
      return timeB - timeA;
    });

    return result;
  }, [usersWithLocation, activeFilters, checkedUsers]);

  // Memoize users with locations
  const usersWithLocations = useMemo(
    () => filteredData.filter((user) => user.location),
    [filteredData]
  );

  // Memoize the API response processing
  const processApiResponse = useMemo(() => {
    return async (response: { data: { data: IUser[] } }) => {
      if (response.data?.data && Array.isArray(response.data.data)) {
        const filteredData = response.data.data.filter((user: IUser) =>
          currentUser ? user.id !== currentUser.id : true
        );

        // Only update if the locations have actually changed
        setUsersWithLocation(prevUsers => {
          if (areUsersEqual({ users: prevUsers }, { users: filteredData })) {
            return prevUsers;
          }
          return filteredData;
        });
        setFilteredUsers(filteredData);
      }
    };
  }, [currentUser]);

  // Fetch users from the same entreprise as the current user
  useEffect(() => {
    const fetchEntrepriseLocations = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(`/location?limit=1000`);
        await processApiResponse(response);
      } catch (error) {
        console.error("Failed to fetch enterprise locations:", error);
        setUsersWithLocation([]);
        setFilteredUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntrepriseLocations();
  }, [processApiResponse]);

  // Trigger a fetch when the page becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);

      if (isVisible) {
        try {
          const response = await axiosInstance.get(`/location?limit=1000`);
          await processApiResponse(response);
        } catch (error) {
          console.error("Failed to fetch enterprise locations:", error);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [processApiResponse]);

  // Add auto-refresh effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const startInterval = () => {
      if (autoRefresh && !intervalId) {
        intervalId = setInterval(async () => {
          try {
            const response = await axiosInstance.get(`/location?limit=1000`);
            await processApiResponse(response);
          } catch (error) {
            console.error("Failed to fetch enterprise locations:", error);
          }
        }, 10000); // 10 seconds
      }
    };

    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    if (isPageVisible) {
      startInterval();
    } else {
      stopInterval();
    }

    return () => {
      stopInterval();
    };
  }, [autoRefresh, processApiResponse, isPageVisible]);


  const { dataGridProps, search, filters } = useDataGrid<
    IUser,
    HttpError,
    IUserFilterVariables
  >({
    initialPageSize: 8,
    onSearch: (params) => {
      setIsFiltering(true);
      setActiveFilters(params);

      // Reset pagination to first page when searching
      setPaginationModel((prev) => ({ ...prev, page: 0 }));

      // Return empty array as we'll handle filtering locally
      return [];
    },
    pagination: { mode: "client" },
  });

  // Update filtered users when activeFilters change
  useEffect(() => {
    if (!isFiltering) return;

    const filtered = usersWithLocation.filter((user) => {
      if (activeFilters.name && activeFilters.name !== "") {
        return user.name
          .toLowerCase()
          .includes(activeFilters.name.toLowerCase());
      }
      return true;
    });

    setFilteredUsers(filtered);
    setIsFiltering(false);
  }, [activeFilters, usersWithLocation, isFiltering]);

  // Get the rows to display in the DataGrid
  const getFilteredRows = () => {
    if (isFiltering) {
      return [];
    }

    const filtered =
      activeFilters.name && activeFilters.name !== ""
        ? filteredUsers
        : usersWithLocation;

    // Let DataGrid handle pagination since it's in client mode
    return filtered;
  };

  const columns = React.useMemo<GridColDef<IUser>[]>(
    () => [
      {
        field: "isSharing",
        headerName: "",
        width: 40,
        minWidth: 40,
        flex: 0,
        filterable: false,
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <Tooltip
              title={
                row.isSharing
                  ? translate("location.status.active")
                  : translate("location.status.inactive")
              }
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  cursor: "help",
                }}
              >
                {row.isSharing ? <NearMeIcon color="success" /> : <NearMeDisabledIcon color="error" />}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "name",
        headerName: translate("user.fields.name"),
        flex: 1,
        minWidth: 80,
      },
      {
        field: "select",
        headerName: "",
        filterable: false,
        sortable: false,
        menuVisible: false,
        width: 40,
        align: "left",
        headerAlign: "right",
        renderHeader: () => (
          <Tooltip title={translate("location.showOnMap")}>
            <VisibilityIcon />
          </Tooltip>
        ),
        renderCell: function render({ row }) {
          return (
            <Checkbox
              checked={checkedUsers.has(row.id)}
              onChange={(e) => {
                const newCheckedUsers = new Set(checkedUsers);
                if (e.target.checked) {
                  newCheckedUsers.add(row.id);
                } else {
                  newCheckedUsers.delete(row.id);
                }
                setCheckedUsers(newCheckedUsers);
              }}
            />
          );
        },
      },
    ],
    [checkedUsers, translate]
  );

  const { register, handleSubmit, reset } = useForm<
    BaseRecord,
    HttpError,
    IUserFilterVariables
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq"),
      name: getDefaultFilter("name", filters, "contains"),
    },
  });

  // Add CSS styles for marker and row highlighting
  const customMarkerStyles = `
    .standard-marker-icon {
      filter: hue-rotate(240deg) saturate(1.5);
    }
    .displayed-on-map {
      background-color: rgba(144, 238, 144, 0.2) !important;
    }
  `;

  // Add styles to document
  const styleElement = document.createElement("style");
  styleElement.textContent = customMarkerStyles;
  document.head.appendChild(styleElement);

  return (
    <Grid container spacing={2} sx={{ height: "100%", overflow: "auto" }}>
      <Grid item xs={12} lg={4}>
        <Card>
          <CardHeader title={translate("location.filters")} />
          <CardContent sx={{ pt: 0 }}>
            <Box
              component="form"
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              autoComplete="off"
              onSubmit={handleSubmit(search)}
            >
              <TextField
                {...register("name")}
                label={translate("location.search")}
                margin="normal"
                fullWidth
                autoFocus
                size="small"
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button type="submit" variant="contained" fullWidth>
                  {translate("location.search")}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setActiveFilters({});
                    search({ name: "" });
                    reset({ name: "" });
                  }}
                >
                  {translate("location.clear")}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Box sx={{ mt: 4 }}>
          <List>
            <DataGrid
              {...dataGridProps}
              rows={getFilteredRows()}
              rowCount={
                !activeFilters.name || activeFilters.name === ""
                  ? usersWithLocation.length
                  : filteredUsers.length
              }
              columns={columns}
              autoHeight
              loading={isLoading || isFiltering}
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[8]}
              paginationMode="client"
              getRowClassName={(params) => {
                const isDisplayedOnMap = checkedUsers.size > 0
                  ? checkedUsers.has(params.row.id)
                  : params.row.isSharing;
                return isDisplayedOnMap ? 'displayed-on-map' : '';
              }}
            />
          </List>
        </Box>
      </Grid>
      <Grid item xs={12} lg={8} sx={{ height: "100%", position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 0,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            padding: '10px',
            gap: 1,
          }}
        >
          <Card
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "8px 16px",
              gap: 2,
            }}
          >
            <Typography>Auto-refresh</Typography>
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
          </Card>

        </Box>

        <Card sx={{ height: "100%" }}>
          <CardContent sx={{ height: "100%", p: '0 !important' }}>
            {isLoading ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <MapView users={usersWithLocations} />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default LocationList;
